import React, { createContext, useCallback, useState, useContext } from "react";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { ref, push as firebasePush, set, get, child } from "firebase/database";
import { database } from "../config/firebase";

export interface StudentProps {
  uid?: string;
  name: string;
  email: string;
  path?: string;
}

interface StudentContextData {
  students: StudentProps[];
  loadingStudent: boolean;
  getAll(userUID: string): Promise<void>;
  createStudent(studentData: StudentProps): Promise<void>;
}

const StudentContext = createContext<StudentContextData>(
  {} as StudentContextData
);

const StudentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const [studentsData, setStudents] = useState([] as StudentProps[]);
  const [loadingStudent, setLoadingStudent] = useState(false);

  const getAll = useCallback(async (userUID: string) => {
    setLoadingStudent(true);

    Swal.fire({
      title: "Carregando...",
      showConfirmButton: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: async () => {
        Swal.showLoading();
        get(child(ref(database), `/users/${userUID}`))
          .then(async (snapshot) => {
            const list: any[] = [];
            snapshot.forEach((current) => {
              list.push(current.val());
            });
            setStudents(list as StudentProps[]);

            Swal.close();
          })
          .catch(() => {
            Swal.hideLoading();
            Swal.fire({
              icon: "error",
              title: "Oops...",
              text: `Aconteceu algum erro inesperado.\nEntre em contato com o suporte técnico.`,
            });
          })
          .finally(() => {
            setLoadingStudent(false);
          });
      },
    });
  }, []);

  const createStudent = useCallback(
    async ({ name, email, path }: StudentProps) => {
      setLoadingStudent(true);
      const type = "student";
      Swal.fire({
        title: "Cadastrando...",
        showConfirmButton: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: async () => {
          Swal.showLoading();
          let criou = false;
          const studentsSnapShot = await get(child(ref(database), "users"));

          const students: StudentProps[] = [];

          studentsSnapShot.forEach((currentSnapShot) => {
            students.push(currentSnapShot.val());
          });
          for (let i = 0; i < students.length; i += 1) {
            if (email === students[i].email) {
              const id = students[i].uid;
              const uid = id;
              set(child(ref(database), `classes/${path}/students/${uid}`), {
                uid,
              })
                .then(() => {
                  Swal.close();
                  Swal.fire({
                    icon: "success",
                    title: "Cadastro de estudante",
                    text: `Estudante cadastrado com sucesso!`,
                    showConfirmButton: false,
                    timer: 1800,
                  }).then(() => navigate(-1));
                })
                .catch(() => {
                  Swal.fire({
                    icon: "error",
                    title: "Oops...",
                    text: "Aconteceu algum erro inesperado.\nEntre em contato com o suporte técnico.",
                  });
                })
                .finally(() => setLoadingStudent(false));
              criou = true;
              break;
            }
          }
          if (!criou) {
            const userRef = firebasePush(child(ref(database), "users"));
            const uid = userRef.key;
            await set(userRef, {
              uid,
              name,
              email,
              type,
            });
            await set(child(ref(database), `classes/${path}/students/${uid}`), {
              uid,
            })
              .then(() => {
                Swal.close();
                Swal.fire({
                  icon: "success",
                  title: "Cadastro de estudante",
                  text: `Estudante cadastrado com sucesso!`,
                  showConfirmButton: false,
                  timer: 1800,
                }).then(() => navigate(-1));
              })
              .catch(() => {
                Swal.fire({
                  icon: "error",
                  title: "Oops...",
                  text: "Aconteceu algum erro inesperado.\nEntre em contato com o suporte técnico.",
                });
              })
              .finally(() => setLoadingStudent(false));
          }
        },
      });
    },
    [navigate]
  );

  const providerValue = React.useMemo(
    () => ({
      getAll,
      createStudent,
      students: studentsData,
      loadingStudent,
    }),
    [getAll, createStudent, studentsData, loadingStudent]
  );

  return (
    <StudentContext.Provider value={providerValue}>
      {children}
    </StudentContext.Provider>
  );
};

function useStudent(): StudentContextData {
  const context = useContext(StudentContext);

  if (!context) {
    throw new Error("useStudent must be used within an AuthProvider");
  }

  return context;
}

export { StudentProvider, useStudent };
